import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  Title1,
} from "@fluentui/react-components";
import {
  ChevronDown24Regular,
  ChevronUp24Regular,
} from "@fluentui/react-icons";
import { MenuLinesIcon } from "icons/MenuLinesIcon";
import { Constants } from "common/constants";
import { PoweredByFooter } from "../PoweredByFooter/PoweredByFooter";
import { useState, useRef, useEffect } from "react";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { useStyles } from "./externalDiagnosticsConsent.styles";
import {
  IExternalDiagnosticsConsentProps,
  ITouchedLinks,
} from "./externalDiagnosticsConsent.types";

const DECLINE_DIALOG_TEXT: string =
  "You have declined the terms and conditions and therefore are unable to complete the diagnostic interview.";

export const TERMS_AND_CONDITIONS_URL: string = "/external-diagnostic/terms";

export const MICROPHONE_HELP_URL: string =
  "/external-diagnostic/microphone-help";

const TERMS_AND_CONS_ACCEPT: string =
  "To continue to the interview, you must accept the Terms and Conditions below.";

export const ExternalDiagnosticsConsent = (
  props: IExternalDiagnosticsConsentProps,
): JSX.Element => {
  const { accentPalette } = props;
  const styles = useStyles();
  const sharedButtonStyles = useSharedButtonStyles();

  const [isDeclineDialogOpen, setIsDeclineDialogOpen] =
    useState<boolean>(false);
  const [touchedLinks, setTouchedLinks] = useState<ITouchedLinks>({
    termsAndConditions: false,
    privacyStatement: false,
  });
  const [isInstructionsOpen, setIsInstructionsOpen] = useState<boolean>(false);
  const declineBtnRef = useRef<HTMLButtonElement>(null);
  const goBackBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isDeclineDialogOpen) {
      setTimeout(() => goBackBtnRef.current?.focus(), 50);
    }
  }, [isDeclineDialogOpen]);

  const handleOnAccept = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    props.handleOnAccept?.(touchedLinks);
  };

  const handleOnDecline = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setIsDeclineDialogOpen(true);
  };

  const handleDeclineDialogClose = (): void => {
    props.handleOnDecline?.();
    setIsDeclineDialogOpen(false);
    setTimeout(() => declineBtnRef.current?.focus(), 0);
  };

  const handleDialogCancel = (): void => {
    setIsDeclineDialogOpen(false);
    setTimeout(() => declineBtnRef.current?.focus(), 0);
  };

  return (
    <div className={styles.root}>
      <div className={styles.contentWrapper}>
        <Title1 className={styles.title}>{props.diagnosticName}</Title1>

        <div
          className={styles.beginningMessage}
          dangerouslySetInnerHTML={{ __html: props.diagnosticBeginningMessage }}
        />

        <div className={styles.collapsibleSection}>
          <button
            type="button"
            className={styles.collapsibleTrigger}
            onClick={() => setIsInstructionsOpen(!isInstructionsOpen)}
            aria-expanded={isInstructionsOpen}
            aria-controls="instructions-content"
          >
            <span className={styles.collapsibleTriggerLabel}>
              <MenuLinesIcon />
              Before You Begin
            </span>
            {isInstructionsOpen ? (
              <ChevronUp24Regular />
            ) : (
              <ChevronDown24Regular />
            )}
          </button>

          <div
            id="instructions-content"
            role="region"
            aria-label="Interview instructions"
            className={styles.instructionsContent}
            hidden={!isInstructionsOpen}
          >
            {props.speechEnabled && (
              <>
                <h2 className={styles.subheading}>Getting Started</h2>
                <ul>
                  <li>
                    <strong>Choose your input method:</strong> You can
                    participate using either keyboard text input or audio
                    through your speaker and microphone. This is selected on the
                    next screen
                  </li>
                </ul>

                <h2 className={styles.subheading}>
                  If Using Audio/Voice Input
                </h2>
                <ul>
                  <li>
                    <strong>View audio setup instructions:</strong>{" "}
                    <Link
                      href={MICROPHONE_HELP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Click here for detailed instructions on audio setup (opens in new tab)"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      Click here for detailed instructions on audio setup
                    </Link>
                  </li>
                  <li>
                    <strong>Enable your microphone:</strong> Ensure your
                    microphone is enabled before beginning
                  </li>
                  <li>
                    <strong>Optimize audio quality:</strong> Take the interview
                    in a quiet environment for optimal voice recognition
                  </li>
                  <li>
                    <strong>Pause and resume audio:</strong> You can pause and
                    resume the audio during the interview if you need more time
                    to think about your answer before responding
                  </li>
                </ul>
              </>
            )}

            <h2 className={styles.subheading}>During the Assessment</h2>
            <ul>
              <li>
                <strong>Stop and resume anytime:</strong> You can stop and
                resume the assessment at any time
              </li>
              <li>
                <strong>Automatic progress saving:</strong> Your progress is
                automatically saved as you go
              </li>
              <li>
                <strong>Return later:</strong> You can return to complete the
                interview at your convenience
              </li>
            </ul>

            <h2 className={styles.subheading}>Important Notes</h2>
            <ul>
              <li>
                <strong>Device consistency:</strong> If you change the device
                you are using for this interview, you will need to start over
                from the beginning
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <p className={styles.acceptTermsText}>{TERMS_AND_CONS_ACCEPT}</p>

        <div className={styles.linksContainer} aria-label="Policy links">
          <Link
            href={TERMS_AND_CONDITIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Terms and Conditions (opens in new tab)"
            onClick={() =>
              setTouchedLinks({ ...touchedLinks, termsAndConditions: true })
            }
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Terms &amp; Conditions
          </Link>
          <span className={styles.linksSeparator} aria-hidden="true">
            |
          </span>
          <Link
            href={Constants.PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy Statement (opens in new tab)"
            onClick={() =>
              setTouchedLinks({ ...touchedLinks, privacyStatement: true })
            }
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Privacy Statement
          </Link>
        </div>

        <div className={styles.actionButtonsContainer}>
          <Button
            ref={declineBtnRef}
            type="button"
            className={sharedButtonStyles.secondaryButton}
            onClick={handleOnDecline}
          >
            Decline
          </Button>
          <Button
            type="button"
            className={sharedButtonStyles.primaryButton}
            style={
              accentPalette
                ? {
                    backgroundColor: accentPalette.accent,
                    color: accentPalette.accentText,
                    borderColor: accentPalette.accent,
                  }
                : undefined
            }
            onClick={handleOnAccept}
          >
            Accept
          </Button>
        </div>
      </div>

      <Dialog
        open={isDeclineDialogOpen}
        onOpenChange={(_: any, d: { open: any }) => {
          if (!d.open) handleDialogCancel();
        }}
      >
        <DialogSurface
          aria-labelledby="decline-title"
          aria-describedby="decline-desc"
        >
          <DialogBody>
            <DialogTitle id="decline-title">Consent Declined</DialogTitle>
            <DialogContent id="decline-desc">
              {DECLINE_DIALOG_TEXT}
            </DialogContent>
            <DialogActions>
              <Button
                ref={goBackBtnRef}
                type="button"
                className={sharedButtonStyles.secondaryButton}
                onClick={handleDialogCancel}
              >
                Go Back
              </Button>
              <Button
                type="button"
                className={sharedButtonStyles.primaryButton}
                onClick={handleDeclineDialogClose}
              >
                Confirm Decline
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <PoweredByFooter />
    </div>
  );
};
